// Minimize/restore for every HUD panel that carries a `.hud-head`.
//
// Also the home of what the player chose: which panels are collapsed, kept
// across a reload beside the positions `hudDrag.ts` stores.
//
// `.hud-minimized` collapses a widget to its own header (one CSS rule keyed
// on `.hud-head` hides every sibling of it). `.hud-stacked` is the second,
// separate class that actually moves it into the top-right stack - kept
// apart so the headless `minimize-nostack` control can withhold just that
// half and prove a collapse-with-no-way-back fails the reading.
//
// Nothing here touches what a panel is DOING: the DOM is hidden by CSS, not
// torn down, so comms.ts keeps logging and the tactical radar keeps ticking
// underneath a minimized header exactly as it does underneath an open one.

import {
  applyHudDocks, clampHudPositions, HUD_RAIL_SELECTORS, pushHudPositionsClearOfPanel,
  resetAllHudPositions, setHudLayoutListener,
} from './hudDrag.ts';
import { devFlag, urlFlag, TEST_SWITCHES } from './urlFlags.ts';
import { track } from './visits/bus.ts';

const MINIMIZE_IDS = ['dev', 'captain', 'schematic', 'comms', 'radar', 'pilot', 'ship-state', 'dv-map'] as const;
type MinimizeId = (typeof MINIMIZE_IDS)[number];

// The panels Michael wants out of the way until he reaches for them. The
// rest boot expanded. This list is the whole boot state - a panel starts
// collapsed by being named here, not by a rule of its own.
const DEFAULT_COLLAPSED: readonly MinimizeId[] = ['captain', 'schematic', 'dev', 'comms', 'radar'];

const STACK_TOP = 12;
const STACK_GAP = 6;

interface HudMinimizeDebug {
  /** The control: collapse the body but never move the header into the stack. */
  noStack?: boolean;
}
let debugState: HudMinimizeDebug = {};

/** Exposed for tools/preview/layout-check.mjs's `--inject minimize-nostack`. */
export function setHudMinimizeDebug(next: HudMinimizeDebug): void {
  debugState = next;
}

declare global {
  interface Window {
    __hudMinimizeDebug?: (next: HudMinimizeDebug) => void;
    /** The control: boot with no default collapsed, for `--inject minimize-default-open`. */
    __hudMinimizeForceExpanded?: boolean;
    /** The control: the rails left to overflow, for `--inject panel-overflow`. */
    __hudFitOff?: boolean;
    /** The control: a place derived from what fits, for `--inject fold-derived`. */
    __hudFoldDerived?: boolean;
    /** The control: nothing stored and nothing read back, for `--inject persist-none`. */
    __hudPersistOff?: boolean;
    /** The control: the fit pass re-taking a fold the player undid, for `--inject refold-undone`. */
    __hudRefoldUndone?: boolean;
    /** The control: a restore that claims no space, for `--inject overlap-blind`. */
    __hudOverlapOff?: boolean;
    /** Reachable from the menu's Settings tab, and from the headless `--persist` arm. */
    __hudResetPanels?: () => void;
    /** The control: a header button that does nothing, for `--inject min-noop`. */
    __hudPaneMinNoop?: boolean;
    /** The control: the fold taking the panel a trip is flown from, for `--inject fold-flying`. */
    __hudFoldFlying?: boolean;
  }
}

/**
 * The control (`?hudrefoldputback=1`): the height fold taking the panel the
 * player just put back, which is how it read before it passed over that one.
 * A rail longer than the window then folds the panel again on the same click
 * and its header button reads dead. What `--transit --inject refold-put-back`
 * has to catch.
 *
 * Read off the URL rather than set on `window` like the fold's other controls,
 * because the arm that reads this one boots the page by URL.
 */
const refoldPutBack = devFlag(() => TEST_SWITCHES && urlFlag('hudrefoldputback'));

const minimized = new Set<MinimizeId>();
const savedStyle = new Map<MinimizeId, string>();

// ---------- What the player chose, across a reload ----------
//
// Only an explicit click is stored. A panel the fold took is derived from the
// window and is re-derived on the next boot, so a layout written at 800x500
// cannot arrive at 2560x1440 with four panels already in the stack.
//
// Every read is defensive on purpose: a payload written by an older build, by
// a different game, or by hand must not be able to strand a panel. Anything
// that is not a known widget id mapped to 'open' or 'closed' is dropped, and
// what survives is a preference, never a position.
const PANEL_KEY = 'hud-panels';
type PanelChoice = 'open' | 'closed';

function loadPanelChoices(): Map<MinimizeId, PanelChoice> {
  const out = new Map<MinimizeId, PanelChoice>();
  if (window.__hudPersistOff) return out;
  let raw: string | null = null;
  try {
    raw = localStorage.getItem(PANEL_KEY);
  } catch {
    return out;
  }
  if (raw === null) return out;
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return out;
  }
  if (typeof parsed !== 'object' || parsed === null || Array.isArray(parsed)) return out;
  for (const [key, value] of Object.entries(parsed as Record<string, unknown>)) {
    if (!(MINIMIZE_IDS as readonly string[]).includes(key)) continue;
    if (value !== 'open' && value !== 'closed') continue;
    out.set(key as MinimizeId, value);
  }
  return out;
}

function savePanelChoice(id: MinimizeId, choice: PanelChoice): void {
  if (window.__hudPersistOff) return;
  const choices = loadPanelChoices();
  choices.set(id, choice);
  try {
    localStorage.setItem(PANEL_KEY, JSON.stringify(Object.fromEntries(choices)));
  } catch {
    // Private mode and a full quota both land here; the session still works,
    // it just will not be remembered.
  }
}

/** Forget every stored panel choice and position, and put the boot layout back. */
export function resetHudPanels(): void {
  try {
    localStorage.removeItem(PANEL_KEY);
  } catch {
    // Nothing to forget if it could not be written in the first place.
  }
  resetAllHudPositions();
  undone.clear();
  foldCover.clear();
  for (const id of MINIMIZE_IDS) {
    autoCollapsed.delete(id);
    markAuto(id, false);
    if (DEFAULT_COLLAPSED.includes(id)) minimize(id);
    else restore(id);
  }
  lastOpened = null;
  fitHudRails();
}

function widgetEl(id: MinimizeId): HTMLElement | null {
  return document.querySelector<HTMLElement>(`[data-widget="${id}"]`);
}

// The stack and `.hud-rail-right` (comms, radar, target) share the same
// right:12px column. A partial stack - captain and schematic collapsed by
// default, comms and radar still in the rail - reaches past the rail's own
// fixed top and lands on it, so the rail's top has to give way. Read once,
// before anything pushes it, so repeated relayouts have a floor to return to.
let railRightDefaultTop: number | null = null;

/** Reassigns every currently-stacked header's `top` so none overlap, and
 *  pushes `.hud-rail-right` down to clear the stack when one is present. */
function relayoutStack(): void {
  let top = STACK_TOP;
  for (const id of MINIMIZE_IDS) {
    if (!minimized.has(id)) continue;
    const el = widgetEl(id);
    if (!el || !el.classList.contains('hud-stacked')) continue;
    el.style.top = `${top}px`;
    top += el.offsetHeight + STACK_GAP;
  }
  const rail = document.querySelector<HTMLElement>('.hud-rail-right');
  if (rail) {
    if (railRightDefaultTop === null) railRightDefaultTop = parseFloat(getComputedStyle(rail).top) || 0;
    rail.style.top = `${Math.max(railRightDefaultTop, top)}px`;
  }
}

// The glyph itself is CSS, drawn off `.hud-minimized` so the ink stays inside
// the button's own box; only the tooltip is left to set here, and it says what
// the CLICK will do - which is what the panel is DRAWN as, whoever collapsed
// it. A button that reads Minimize on a panel already drawn collapsed is the
// dead button the player has nothing left to press.
function setButtonState(id: MinimizeId): void {
  const btn = document.querySelector<HTMLButtonElement>(`.hud-min-btn[data-min="${id}"]`);
  if (!btn) return;
  btn.title = minimized.has(id) ? 'Restore' : 'Minimize';
}

function minimize(id: MinimizeId): void {
  const el = widgetEl(id);
  if (!el || minimized.has(id)) return;
  savedStyle.set(id, el.getAttribute('style') ?? '');
  minimized.add(id);
  el.classList.add('hud-minimized');
  if (!debugState.noStack) el.classList.add('hud-stacked');
  relayoutStack();
  setButtonState(id);
}

function restore(id: MinimizeId): void {
  const el = widgetEl(id);
  if (!el || !minimized.has(id)) return;
  minimized.delete(id);
  el.classList.remove('hud-minimized', 'hud-stacked');
  const prev = savedStyle.get(id) ?? '';
  if (prev) el.setAttribute('style', prev);
  else el.removeAttribute('style');
  savedStyle.delete(id);
  relayoutStack();
  setButtonState(id);
}

// The button goes on what the panel is DRAWN as: collapsed opens, open closes.
// A panel the fold took is drawn collapsed and its header button is the only
// thing the player has left of it, so the click has to put it back - and the
// fold it undoes is not offered again for the same cover, or the next pass
// would take it back in the same breath and the button would read dead.
function toggle(id: MinimizeId): void {
  const opening = minimized.has(id);
  track('ui_open', { ui: id, action: opening ? 'open' : 'close' });
  if (opening) {
    lastOpened = id;
    if (autoCollapsed.has(id)) {
      const cover = foldCover.get(id);
      if (cover === undefined) undone.delete(id);
      else undone.set(id, cover);
      autoCollapsed.delete(id);
      markAuto(id, false);
    }
    restore(id);
    savePanelChoice(id, 'open');
  } else {
    if (lastOpened === id) lastOpened = null;
    undone.delete(id);
    autoCollapsed.delete(id);
    markAuto(id, false);
    minimize(id);
    savePanelChoice(id, 'closed');
  }
  fitHudRails();
  // A panel the player put back has to have somewhere to go. The fit pass
  // reaches only panels still in a rail, so a floating one is moved off the box
  // that just grew back rather than left sitting on it, and the rails are
  // derived again once it has: which panel covers which is what the fold reads.
  if (!opening || window.__hudOverlapOff) return;
  if (pushHudPositionsClearOfPanel(MINIMIZE_IDS, id) > 0) fitHudRails();
}

export interface PaneMinimizeHandle {
  dispose(): void;
  /**
   * Collapse or restore the panel on behalf of the layout itself, rather than
   * the player's own click - `setAuto(true)` does nothing if the panel is
   * already minimized (the player's own choice, or an earlier auto-collapse),
   * and `setAuto(false)` only restores it where this same mechanism is what
   * minimized it, never a collapse the player chose with the button.
   */
  setAuto(collapse: boolean): void;
}

/**
 * The same collapse and restore, for a panel that is in no rail.
 *
 * The Atlas draws its panels into the menu pane, where `.hud-stacked` would
 * carry a collapsed header off to the window's top-right corner and out of the
 * pane entirely, and where the fold pass has no rail to read. So a pane panel
 * gets the half that is the button: the `.hud-head` collapse rule, the same
 * glyph, and a tooltip that says what the click will do. Returns its own
 * teardown, since the Atlas frees what it makes.
 */
export function wirePaneMinimize(el: HTMLElement, btn: HTMLButtonElement): PaneMinimizeHandle {
  let auto = false;
  const sync = (): void => {
    btn.title = el.classList.contains('hud-minimized') ? 'Restore' : 'Minimize';
  };
  const onClick = (e: Event): void => {
    e.stopPropagation();
    if (window.__hudPaneMinNoop) return;
    auto = false;
    el.classList.toggle('hud-minimized');
    sync();
  };
  btn.addEventListener('click', onClick);
  sync();
  return {
    dispose: () => btn.removeEventListener('click', onClick),
    setAuto(collapse: boolean): void {
      if (collapse) {
        if (el.classList.contains('hud-minimized')) return;
        el.classList.add('hud-minimized');
        auto = true;
      } else if (auto) {
        el.classList.remove('hud-minimized');
        auto = false;
      }
      sync();
    },
  };
}

/** The same collapse and restore the panel's own header button does, for a key
 *  binding to call. An id no panel carries is ignored. */
export function toggleHudPanel(id: string): void {
  if (!(MINIMIZE_IDS as readonly string[]).includes(id)) return;
  toggle(id as MinimizeId);
}

// ---------- Fitting a rail into the window it is drawn in ----------
//
// The rails are columns of fixed-height panels in a window that is not. At
// 800x500 the left rail's own content runs a couple of hundred px past the
// bottom edge with every panel open, so panels stacked past the fold and onto
// each other: a header under another panel takes no
// click, and one below the fold cannot be reached at all.
//
// A rail cannot scroll its way out of that - it is `pointer-events: none` on
// purpose, so the 220px column it occupies does not eat mouse-look - so what
// it does instead is change shape: the bottom-most panel that does not fit
// folds into the same top-right stack the minimize button uses, where its
// header is on screen and answers for itself.

// Top to bottom within each rail is the DOM's own order, read into `homes`.
const RAIL_SELECTORS: readonly string[] = HUD_RAIL_SELECTORS;
const FIT_GAP = 6;

/** The lowest y a panel's box may reach: the window's foot, or the action bar's top where the bar runs under the panel's columns. */
function floorFor(r: DOMRect): number {
  const foot = window.innerHeight - FIT_GAP;
  const b = document.querySelector<HTMLElement>('[data-widget="action-bar"]')?.getBoundingClientRect();
  if (b && b.width > 0 && b.height > 0 && b.top > r.top && r.left < b.right && r.right > b.left) return Math.min(foot, b.top - FIT_GAP);
  return foot;
}

/** Where a panel lives: its rail, and its slot down that rail, read from the
 *  DOM order. A fit pass is a function of the window, the panels the player has
 *  open and this order alone, so a fold and a restore land on the same rails
 *  whatever sequence got there. Re-read on every pass because a drop into a
 *  rail is a DOM move (src/hudDrag.ts), which is the one thing that changes it. */
interface PanelHome {
  rail: number;
  slot: number;
}
const homes = new Map<MinimizeId, PanelHome>();

function readHomes(): void {
  homes.clear();
  RAIL_SELECTORS.forEach((sel, rail) => {
    const railEl = document.querySelector(sel);
    if (!railEl) return;
    let slot = 0;
    railEl.querySelectorAll<HTMLElement>('[data-widget]').forEach((el) => {
      const id = el.dataset['widget'];
      if (id === undefined || !(MINIMIZE_IDS as readonly string[]).includes(id)) return;
      homes.set(id as MinimizeId, { rail, slot: slot++ });
    });
  });
}

/** Folded to make the rail fit, rather than by the player. Kept apart so a
 *  reload restores what the player chose and re-derives the rest. */
const autoCollapsed = new Set<MinimizeId>();
/** Which panel was sitting on a folded panel's header when the fold took it.
 *  Derived with the rest of the arrangement, and dropped with it. */
const foldCover = new Map<MinimizeId, string>();
/** A fold the player undid with the panel's own button, against the cover it
 *  was taken for. The pass offers that one back rather than taking it again,
 *  which is the difference between a panel the player can reach and a button
 *  that does nothing. A fold the window's own height called for is not in
 *  here: there the pass has no other answer. */
const undone = new Map<MinimizeId, string>();
/** The panel the player opened most recently, which neither fold takes: the
 *  height rule otherwise lands on it in the same breath as the restore and its
 *  header button reads dead. One panel at a time, and it moves to whichever
 *  panel is put back next, so no panel is exempt for longer than that. */
let lastOpened: MinimizeId | null = null;

export function isAutoCollapsed(id: string): boolean {
  return autoCollapsed.has(id as MinimizeId);
}

/** Says so on the element, so a reader can tell a rail that changed shape from
 *  a panel the player closed - and so a stored layout never keeps one. */
function markAuto(id: MinimizeId, on: boolean): void {
  const el = widgetEl(id);
  if (!el) return;
  if (on) el.dataset['hudAutocollapsed'] = '1';
  else delete el.dataset['hudAutocollapsed'];
}

/** The rail a panel is flowing down, or null for one that is in no rail and
 *  for one the player dragged out of its rail's flow - folding a rail buys
 *  neither of those anything. */
function railOf(el: HTMLElement, id: string | undefined): number | null {
  if (el.classList.contains('repositioned')) return null;
  const home = id === undefined ? undefined : homes.get(id as MinimizeId);
  return home === undefined ? null : home.rail;
}

/** A covered panel is only an answer for the fold when the bottom-most rule
 *  could have picked it too: in that rail, open, in the flow, and with a box.
 *  A panel in the stack, one the player dragged out itself, or one with no box
 *  leaves the rail its bottom-most panel instead. */
function coverFoldable(rail: number, id: MinimizeId): boolean {
  if (homes.get(id)?.rail !== rail) return false;
  const el = widgetEl(id);
  if (!el || minimized.has(id) || el.classList.contains('repositioned')) return false;
  return el.getBoundingClientRect().height !== 0;
}

/** The rails the window cannot hold, by the two things a rail has to answer
 *  for, read the way the headless arm reads them: nothing below the fold, and
 *  no panel's header under another panel. Anything else - a body overlapping a
 *  body by a few px - is not a fault and does not cost a panel its place.
 *
 *  A rail's value is the panel the fold is to take when the only fault the
 *  rail has is a header under a panel the player dragged out of the flow, and
 *  null when the rail is also past the floor or is covered by a panel still in
 *  the layout: the player's own drag costs the rail the panel it covers, and
 *  nothing else. Where more than one of a rail's panels is covered, the value
 *  is the one with the highest home slot, so the pick does not depend on the
 *  order the DOM answered in. */
interface CoverPick {
  id: MinimizeId;
  cover: string;
}

function overflowingRails(): Map<number, CoverPick | null> {
  const covered = new Map<number, CoverPick[]>();
  const plain = new Set<number>();
  for (const id of MINIMIZE_IDS) {
    const el = widgetEl(id);
    if (!el) continue;
    const r = el.getBoundingClientRect();
    if (r.width === 0 && r.height === 0) continue;
    const rail = railOf(el, id);
    if (rail !== null && r.bottom > floorFor(r)) { plain.add(rail); continue; }
    const head = el.querySelector<HTMLElement>(':scope > .hud-head');
    if (!head) continue;
    const hr = head.getBoundingClientRect();
    if (hr.width === 0 || hr.height === 0) continue;
    const hit = document.elementFromPoint(hr.left + hr.width / 2, hr.top + hr.height / 2);
    const cover = hit && hit !== el && !el.contains(hit) ? hit.closest<HTMLElement>('[data-widget]') : null;
    if (!cover) continue;
    // A dragged panel belongs to no rail of its own, so only the covered
    // panel's rail is asked, and it is asked for the panel the player covered
    // rather than for the one at the bottom of the column.
    if (cover.classList.contains('repositioned')) {
      // A press pins a panel at its own drawn rect before the pointer has gone
      // anywhere, and the column under it rises into the space it left: that
      // is the rail reflowing, not the player covering anything. Only a panel
      // the pointer actually carried somewhere (src/hudDrag.ts) costs the rail
      // the panel it lands on.
      if (!window.__hudPressIsDrag && cover.dataset['hudDragged'] !== '1') continue;
      if (rail === null) continue;
      const coverId = cover.dataset['widget'] ?? '';
      if (!window.__hudRefoldUndone && undone.get(id) === coverId) continue;
      if (!coverFoldable(rail, id)) { plain.add(rail); continue; }
      const list = covered.get(rail) ?? [];
      list.push({ id, cover: coverId });
      covered.set(rail, list);
      continue;
    }
    // Either rail giving way clears a header drawn under another panel, so
    // both are asked: the covered panel's own, and where the cover came from.
    if (rail !== null) plain.add(rail);
    const coverRail = railOf(cover, cover.dataset['widget']);
    if (coverRail !== null) plain.add(coverRail);
  }
  const out = new Map<number, CoverPick | null>();
  for (const rail of plain) out.set(rail, null);
  for (const [rail, list] of covered) {
    if (out.has(rail)) continue;
    let pick: CoverPick | null = null;
    for (const entry of list) {
      if (pick === null || (homes.get(entry.id)?.slot ?? -1) > (homes.get(pick.id)?.slot ?? -1)) pick = entry;
    }
    if (pick !== null) out.set(rail, pick);
  }
  return out;
}

// A panel drawing a `[data-hud-keep]` control is one a trip is being picked or
// flown from: the delta-v map while it offers GO or while GO holds the stick,
// whose buttons a fold would leave as 0x0 boxes. Neither fold takes it. The
// `[data-hud-shrink]` list it is drawing gives up height first, and only then
// do the rail's other open panels fold.
const SHRINK_FLOOR = 40;

function inUse(id: MinimizeId): boolean {
  if (window.__hudFoldFlying) return false;
  const keeps = widgetEl(id)?.querySelectorAll<HTMLElement>('[data-hud-keep]') ?? [];
  return Array.from(keeps).some((keep) => keep.getClientRects().length > 0);
}

/** Lifts every list's cap and returns what puts each list's scroll back: a list
 *  let out to its whole height has nothing to scroll, so its scrollTop drops to 0. */
function unshrinkLists(): () => void {
  const kept = Array.from(document.querySelectorAll<HTMLElement>('[data-hud-shrink]'), (el) => ({ el, top: el.scrollTop }));
  for (const { el } of kept) el.style.maxHeight = '';
  return () => {
    for (const { el, top } of kept) if (el.scrollTop !== top) el.scrollTop = top;
  };
}

function shrinkFloor(el: HTMLElement): number {
  return Number(el.dataset['hudShrink']) || SHRINK_FLOOR;
}

/** Take a rail's overhang out of the list of the panel in use in it, down to
 *  the px its `data-hud-shrink` names, or `SHRINK_FLOOR` where it names none.
 *  A `[data-hud-shrink]` nested inside another one (the delta-v map's trip
 *  list inside its shared scroll box) is tried first, so the inner list
 *  gives up height before the box holding it does; only once the inner one
 *  is at its own floor does the outer one start giving up its own.
 *  True when the list gave any height. */
function shrinkInUse(rail: number): boolean {
  let past = 0;
  let list: HTMLElement | null = null;
  for (const [id, home] of homes) {
    if (home.rail !== rail || minimized.has(id)) continue;
    const el = widgetEl(id);
    if (!el || el.classList.contains('repositioned')) continue;
    const r = el.getBoundingClientRect();
    if (r.height === 0) continue;
    past = Math.max(past, r.bottom - floorFor(r));
    if (list === null && inUse(id)) {
      list = Array.from(el.querySelectorAll<HTMLElement>('[data-hud-shrink]'))
        .reverse()
        .find((l) => l.getClientRects().length > 0 && l.getBoundingClientRect().height > shrinkFloor(l))
        ?? null;
    }
  }
  if (list === null || past <= 0) return false;
  const h = list.getBoundingClientRect().height;
  const listFloor = shrinkFloor(list);
  const want = Math.max(listFloor, Math.floor(h - past));
  if (want >= h) return false;
  list.style.maxHeight = `${want}px`;
  return true;
}

/** The bottom-most panel still open in a rail, in home order - the last slot
 *  is the first to go, so the rail folds from the bottom up. `skip` and a
 *  panel in use are passed over whatever slot they hold, and the scan carries
 *  on past them; the rail is left over-long only where nothing else is open. */
function bottomMostOpen(rail: number, skip: MinimizeId | null = null): MinimizeId | null {
  let pick: MinimizeId | null = null;
  let pickSlot = -1;
  for (const [id, home] of homes) {
    if (home.rail !== rail || home.slot <= pickSlot || minimized.has(id) || id === skip) continue;
    if (inUse(id)) continue;
    const el = widgetEl(id);
    if (!el || el.classList.contains('repositioned')) continue;
    if (el.getBoundingClientRect().height === 0) continue;
    pick = id;
    pickSlot = home.slot;
  }
  return pick;
}

/** Writes the top band's bottom edge, which the left rail starts a gutter
 *  below, and puts the key list's `?` beside the action bar. A rail the fit
 *  pass is about to measure starts where it will be drawn, so this runs first. */
function clearFixedPanels(): void {
  const root = document.documentElement;
  const band = document.querySelector<HTMLElement>('.hud-band-top')?.getBoundingClientRect();
  if (band && band.height > 0) root.style.setProperty('--hud-band-top-bottom', `${band.bottom}px`);

  placeKeysGlyphNow();
}

/** Just right of the bar, centred on it; left of it where a panel or the window edge is in the way; above its right end as the last resort. */
function placeKeysGlyph(glyph: HTMLElement, b: DOMRect): void {
  if (b.width === 0) return;
  const gap = 8;
  const g = glyph.getBoundingClientRect();
  const top = b.top + (b.height - g.height) / 2;
  const panels = [...document.querySelectorAll<HTMLElement>('[data-widget]')]
    .filter((w) => w.dataset.widget !== 'action-bar')
    .map((w) => w.getBoundingClientRect())
    .filter((r) => r.width > 0 && r.height > 0);
  const clear = (x: number): boolean => x >= gap && x + g.width <= window.innerWidth - gap
    && !panels.some((r) => x < r.right && x + g.width > r.left && top < r.bottom && top + g.height > r.top);
  const right = b.right + gap;
  const left = b.left - gap - g.width;
  const beside = clear(right) ? right : clear(left) ? left : null;
  glyph.style.left = `${Math.round(beside ?? b.right - g.width)}px`;
  glyph.style.top = `${Math.round(beside === null ? b.top - gap - g.height : top)}px`;
}

function placeKeysGlyphNow(): void {
  const bar = document.querySelector<HTMLElement>('[data-widget="action-bar"]');
  const glyph = document.getElementById('keys-glyph');
  if (bar && glyph) placeKeysGlyph(glyph, bar.getBoundingClientRect());
}

/** Fold whatever the window cannot hold.
 *
 *  Every pass puts each folded panel back in its home slot first and derives
 *  the whole arrangement again, so one window size and one set of open panels
 *  always give the same rails however they got there. The cost is that a panel
 *  the player collapses frees room and can bring a folded one back in its
 *  place, which is the same rule read the other way round.
 *
 *  `unfold` reaches only the derived control below; the home pass offers every
 *  folded panel back on every pass either way.
 *
 *  A rail the `overflowingRails` map answered with a covered panel is answered
 *  once for the call. The column is top-aligned, so the panel below the cover
 *  rises into the spot the cover is sitting on and would be read as covered
 *  itself, and the rail would fold its way down to the same arrangement the
 *  bottom-most rule reaches from the other end. */
export function fitHudRails(unfold = false): void {
  clearFixedPanels();
  if (window.__hudFitOff) return;
  readHomes();
  // A stored position is applied before the panel it belongs to has a size, so
  // this is where one that no longer fits the window is brought back in.
  clampHudPositions();
  applyHudDocks();
  const rescroll = unshrinkLists();
  if (window.__hudFoldDerived) fitDerived(unfold);
  else foldToHomes();
  rescroll();
  placeKeysGlyphNow();
}

function foldToHomes(): void {
  for (const id of [...autoCollapsed]) {
    autoCollapsed.delete(id);
    foldCover.delete(id);
    markAuto(id, false);
    restore(id);
  }
  const answered = new Set<number>();
  for (let pass = 0; pass < MINIMIZE_IDS.length; pass++) {
    const over = overflowingRails();
    if (over.size === 0) return;
    let took = false;
    for (const [rail, hint] of over) {
      if (hint !== null && answered.has(rail)) continue;
      if (hint === null && shrinkInUse(rail)) { took = true; continue; }
      // The panel the player just put back is not the one to take: the fold
      // lands on it in the same breath and its header button reads dead.
      const victim = hint === null
        ? bottomMostOpen(rail, refoldPutBack() ? null : lastOpened)
        : hint.id;
      if (victim === null) continue;
      if (hint !== null) {
        answered.add(rail);
        foldCover.set(victim, hint.cover);
      }
      autoCollapsed.add(victim);
      minimize(victim);
      markAuto(victim, true);
      took = true;
    }
    if (!took) return;
  }
}

// ---------- The control: a place derived from what fits ----------
//
// `--inject fold-derived` puts back the fold the home slots replaced, so the
// reading that says a panel comes back where it lives can fail. There is no
// home here: the fold takes whichever open panel reaches furthest down the
// window, keeps the panel the player opened last for as long as it stands, and
// offers a folded panel back only when the window itself resizes. Two fits of
// the same window with the same panels open therefore differ by the order the
// folds happened in, which is what the restore reading catches.

function railsOverflow(): boolean {
  for (const id of MINIMIZE_IDS) {
    const el = widgetEl(id);
    if (!el) continue;
    const r = el.getBoundingClientRect();
    if (r.width === 0 && r.height === 0) continue;
    if (!el.classList.contains('repositioned') && r.bottom > floorFor(r)) return true;
    const head = el.querySelector<HTMLElement>(':scope > .hud-head');
    if (!head) continue;
    const hr = head.getBoundingClientRect();
    if (hr.width === 0 || hr.height === 0) continue;
    const hit = document.elementFromPoint(hr.left + hr.width / 2, hr.top + hr.height / 2);
    if (hit && hit !== el && !el.contains(hit) && hit.closest('[data-widget]')) return true;
  }
  return false;
}

/** The open rail panel reaching furthest down the window. `skip` keeps the
 *  panel the player just opened. */
function foldable(skip: MinimizeId | null): MinimizeId | null {
  let worst: MinimizeId | null = null;
  let worstBottom = -Infinity;
  for (const [id] of homes) {
    if (id === skip || minimized.has(id)) continue;
    const el = widgetEl(id);
    if (!el || el.classList.contains('repositioned')) continue;
    const r = el.getBoundingClientRect();
    if (r.height === 0) continue;
    if (r.bottom > worstBottom) { worstBottom = r.bottom; worst = id; }
  }
  return worst;
}

function fitDerived(unfold: boolean): void {
  if (unfold) {
    for (const id of [...autoCollapsed]) {
      autoCollapsed.delete(id);
      markAuto(id, false);
      restore(id);
    }
  }
  for (let pass = 0; pass < MINIMIZE_IDS.length; pass++) {
    if (!railsOverflow()) return;
    const victim = foldable(lastOpened);
    if (victim === null) break;
    autoCollapsed.add(victim);
    minimize(victim);
    markAuto(victim, true);
  }
  markOverflow();
}

/** The one panel the derived fold can be left unable to place: the one the
 *  player opened last, which it will not close behind them. It says so on
 *  itself so the headless reading can tell it from a panel nobody chose to
 *  overflow. The home fold passes over that panel too but marks nothing: it
 *  folds on down the rail past it, so a rail is left over-long only where that
 *  panel is the only one open in it. */
function markOverflow(): void {
  for (const id of MINIMIZE_IDS) {
    const el = widgetEl(id);
    if (el) delete el.dataset['hudOverflow'];
  }
  if (lastOpened === null || minimized.has(lastOpened) || !railsOverflow()) return;
  const el = widgetEl(lastOpened);
  if (el) el.dataset['hudOverflow'] = '1';
}

let fitQueued = false;
let fitWantsUnfold = false;
function queueFit(unfold = false): void {
  if (unfold) fitWantsUnfold = true;
  if (fitQueued) return;
  fitQueued = true;
  // Out of the observer's own callback: folding a panel resizes the rail that
  // fired it, and Chrome reports that as an undelivered-notification error.
  requestAnimationFrame(() => {
    fitQueued = false;
    fitHudRails(fitWantsUnfold);
    fitWantsUnfold = false;
  });
}

export function initHudMinimize(): void {
  window.__hudMinimizeDebug = setHudMinimizeDebug;
  readHomes();
  document.querySelectorAll<HTMLButtonElement>('.hud-min-btn[data-min]').forEach((btn) => {
    const id = btn.dataset['min'] as MinimizeId | undefined;
    if (!id || !MINIMIZE_IDS.includes(id)) return;
    btn.addEventListener('click', (e) => {
      e.stopPropagation();
      toggle(id);
    });
  });
  if (!window.__hudMinimizeForceExpanded) {
    // The default decides only where the player has said nothing. A panel
    // deliberately opened stays open even after the default for it changes.
    const choices = loadPanelChoices();
    for (const id of MINIMIZE_IDS) {
      const choice = choices.get(id) ?? (DEFAULT_COLLAPSED.includes(id) ? 'closed' : 'open');
      if (choice === 'closed') minimize(id);
    }
  }
  window.__hudResetPanels = resetHudPanels;
  setHudLayoutListener(() => queueFit());
  window.addEventListener('resize', () => queueFit(true));
  // The pilot panel ships hidden and is revealed a beat after the menu closes,
  // and several panels draw their own contents later still, so the rail that
  // fits at boot is not the rail the player ends up with.
  const ro = new ResizeObserver(() => queueFit());
  for (const sel of RAIL_SELECTORS) {
    const el = document.querySelector(sel);
    if (el) ro.observe(el);
  }
  const band = document.querySelector('.hud-band-top');
  if (band) ro.observe(band);
  // A fight's cooldown readouts resize the bar every frame, and the bar is in
  // no rail, so it gets the placement alone rather than a whole fit.
  const bar = document.querySelector('[data-widget="action-bar"]');
  if (bar) new ResizeObserver(() => requestAnimationFrame(clearFixedPanels)).observe(bar);
  queueFit();
}
